import { Link } from '@tanstack/react-router';
import { Printer } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { EMPTY, formatDateTime } from '../../../lib/format';
import { useExamSession } from '../api/exam-sessions-api';
import styles from './PrintRoster.module.css';

/**
 * ASM-05's printable list (acceptance criterion 3) for the invigilator: seat, name, and
 * room to sign and to write the mark. No contact details — the invigilator may not see
 * them (§L23). Seats are numbered by name, as the session page numbers them.
 */
export function PrintRosterPage({ sessionId }: { readonly sessionId: string }): ReactNode {
  const query = useExamSession(sessionId);

  if (query.isPending) return <SkeletonLines count={10} />;
  if (query.error) {
    if (query.error.status === 403) return <ForbiddenState resource="this exam session" contact="The recruiter who set it" />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  const session = query.data;
  const seated = session.roster.filter((row) => row.status !== 'rescheduled');

  return (
    <article className={styles.sheet}>
      <div className={styles.toolbar}>
        <Link to="/assessments/sessions/$sessionId" params={{ sessionId }}>
          ← Back to the session
        </Link>
        <Button variant="solid" icon={Printer} onClick={() => window.print()}>
          Print
        </Button>
      </div>
      <h1 className={styles.title}>{session.name}</h1>
      <dl className={styles.facts}>
        <div>
          <dt>Job</dt>
          <dd>{session.jobTitle}</dd>
        </div>
        <div>
          <dt>When</dt>
          <dd>{session.startsAt ? formatDateTime(session.startsAt) : EMPTY}</dd>
        </div>
        <div>
          <dt>Where</dt>
          <dd>{session.venue ?? EMPTY}</dd>
        </div>
        <div>
          <dt>Invigilator</dt>
          <dd>{session.invigilatorName ?? EMPTY}</dd>
        </div>
        <div>
          <dt>Paper</dt>
          <dd>
            {session.testName}, out of {session.maxScore}
          </dd>
        </div>
        <div>
          <dt>Booked</dt>
          <dd>{seated.length}</dd>
        </div>
      </dl>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.seat}>Seat</th>
              <th>Name</th>
              <th className={styles.blank}>Signature</th>
              <th>Mark (of {session.maxScore})</th>
            </tr>
          </thead>
          <tbody>
            {seated.map((row) => (
              <tr key={row.attendanceId}>
                <td className={styles.seat}>{row.seat}</td>
                <td>{row.candidateName}</td>
                <td />
                <td>{row.score ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={styles.footer}>Printed {formatDateTime(new Date())}. Anyone absent is a no-show, not a fail — mark them so in the app.</p>
    </article>
  );
}
